<!-- Header: Date navigation + Edit targets button -->
<div class="d-flex w-100 m-b-4 align-items-center">
    <app-date-navigation
        [dateNavigationOptions]="dateNavigationOptions"
        (selectedDateUpdated)="updateSelectedDate()"
        class="w-100 m-r-4">
    </app-date-navigation>

    <div class="data-source-toggle m-r-3 d-flex align-items-center" style="gap: 8px;">
        <span class="toggle-label" [class.toggle-label--active]="dataSource === 'RESUMO'">Resumo</span>
        <label class="switch">
            <input type="checkbox"
                   [checked]="dataSource === 'YUKI'"
                   (change)="toggleDataSource()"
                   [disabled]="loadingData || loadingYukiVerification">
            <span class="slider"></span>
        </label>
        <span class="toggle-label" [class.toggle-label--active]="dataSource === 'YUKI'">Yuki</span>
    </div>

    <button class="button button--outline d-flex align-items-center nowrap m-r-2"
            (click)="sendOverviewEmail()"
            [disabled]="sendingEmail || loadingData || overviewData.length === 0">
        <i class="las la-envelope m-r-1" *ngIf="!sendingEmail"></i>
        <i class="las la-spinner la-spin m-r-1" *ngIf="sendingEmail"></i>
        Send Email
    </button>

    <button class="button button--primary d-flex align-items-center nowrap" (click)="openTargetModal()">
        <i class="las la-bullseye m-r-1"></i>
        Edit Targets
    </button>
</div>

<!-- Loading -->
<div *ngIf="loadingData">
    <app-loading-message></app-loading-message>
</div>

<!-- Empty state -->
<div *ngIf="!loadingData && overviewData.length === 0">
    <app-message-empty [title]="'No turnover data available for ' + currentYear + '.'" [icon]="'../../assets/img/open-box.png'"></app-message-empty>
</div>

<!-- Main Turnover Matrix -->
<div *ngIf="!loadingData && overviewData.length > 0">

    <!-- Business Developer Target Meter -->
    <section class="business-developer-meter m-b-4" *ngIf="businessDeveloperOverviewData.length > 0">
        <div class="d-flex align-items-center m-b-3 business-developer-meter__header">
            <div class="form-group m-b-0">
                <label>Business developer</label>
                <select [(ngModel)]="selectedBusinessDeveloperUuid">
                    <option [ngValue]="'ALL'">All</option>
                    <option *ngFor="let businessDeveloper of businessDeveloperOverviewData" [ngValue]="businessDeveloper.businessDeveloperUuid">
                        {{ businessDeveloper.businessDeveloperName }}
                    </option>
                </select>
            </div>
            <button class="button button--outline d-flex align-items-center nowrap" (click)="openBusinessDeveloperTargetModal()">
                <i class="las la-bullseye m-r-1"></i>
                Edit Business Developer Targets
            </button>
        </div>

        <ng-container *ngIf="selectedBusinessDeveloper as businessDeveloper">
            <div class="d-flex m-b-3" style="gap: 2rem; flex-wrap: wrap;">
                <div class="box">
                    <label class="m-b-1">Portfolio revenue {{ currentYear }}</label>
                    <div class="summary-value summary-value--primary">{{ formatCurrency(businessDeveloper.yearTotal) }}</div>
                </div>
                <div class="box">
                    <label class="m-b-1">Target {{ currentYear }}</label>
                    <div class="summary-value">{{ formatCurrency(businessDeveloper.yearTargetTotal) }}</div>
                </div>
                <div
                    class="box box--clickable"
                    role="button"
                    tabindex="0"
                    (click)="openSelectedBusinessDeveloperMarginOverview(businessDeveloperMarginOverviewModal)"
                    (keydown.enter)="openSelectedBusinessDeveloperMarginOverview(businessDeveloperMarginOverviewModal)">
                    <label class="m-b-1">Portfolio margin</label>
                    <div class="summary-value" [title]="getSelectedBusinessDeveloperMarginTooltip()" [ngClass]="{
                        'summary-value--success': getSelectedBusinessDeveloperRealizedMargin() >= 0,
                        'summary-value--danger': getSelectedBusinessDeveloperRealizedMargin() < 0
                    }">
                        {{ formatCurrency(getSelectedBusinessDeveloperRealizedMargin()) }}
                    </div>
                </div>
                <div class="box">
                    <label class="m-b-1">ROI result</label>
                    <div class="summary-value" [ngClass]="{
                        'summary-value--success': getSelectedBusinessDeveloperAchievement() >= 0,
                        'summary-value--danger': getSelectedBusinessDeveloperAchievement() < 0
                    }">
                        {{ formatCurrency(getSelectedBusinessDeveloperAchievement()) }}
                    </div>
                </div>
                <div
                    class="box box--clickable"
                    role="button"
                    tabindex="0"
                    [title]="getSelectedBusinessDeveloperCostTooltip()"
                    (click)="openSelectedBusinessDeveloperCostOverview(businessDeveloperCostOverviewModal)"
                    (keydown.enter)="openSelectedBusinessDeveloperCostOverview(businessDeveloperCostOverviewModal)">
                    <label class="m-b-1">Cost {{ currentYear }} <i class="las la-info-circle"></i></label>
                    <div class="summary-value">{{ formatCurrency(businessDeveloper.yearCostTotal) }}</div>
                    <span class="summary-hint">Click for breakdown</span>
                </div>
                <div class="box">
                    <label class="m-b-1">Target progress</label>
                    <div class="summary-value" [ngClass]="{
                        'summary-value--success': businessDeveloper.yearTargetTotal > 0 && businessDeveloper.yearTotal >= businessDeveloper.yearTargetTotal,
                        'summary-value--danger': businessDeveloper.yearTargetTotal > 0 && businessDeveloper.yearTotal < businessDeveloper.yearTargetTotal
                    }">
                        {{ businessDeveloper.yearTargetTotal > 0 ? getSelectedBusinessDeveloperTargetPercentage() + '%' : '-' }}
                    </div>
                </div>
                <div class="box">
                    <label class="m-b-1">Payback</label>
                    <div class="summary-value">{{ getSelectedBusinessDeveloperPaybackLabel() }}</div>
                </div>
            </div>

            <div class="business-developer-executive m-b-3">
                <div class="business-developer-executive__header">
                    <div>
                        <div class="business-developer-executive__title">
                            {{ businessDeveloper.businessDeveloperName }} is
                            <span [ngClass]="{
                                'text-success': getSelectedBusinessDeveloperAchievement() >= 0,
                                'text-danger': getSelectedBusinessDeveloperAchievement() < 0
                            }">{{ getSelectedBusinessDeveloperProfitabilityStatus() }}</span>
                            in {{ currentYear }}
                        </div>
                        <div class="business-developer-executive__subtitle">
                            {{ getSelectedBusinessDeveloperTypeLabel() }} cost source:
                            <strong>{{ getSelectedBusinessDeveloperCostSourceLabel() }}</strong>
                        </div>
                    </div>
                    <div class="business-developer-executive__status" [ngClass]="{
                        'business-developer-executive__status--success': getSelectedBusinessDeveloperAchievement() >= 0,
                        'business-developer-executive__status--danger': getSelectedBusinessDeveloperAchievement() < 0
                    }">
                        {{ formatCurrency(getSelectedBusinessDeveloperAchievement()) }}
                    </div>
                </div>

                <div class="business-developer-executive__grid">
                    <div class="business-developer-executive__metric">
                        <label>Portfolio revenue {{ currentYear }}</label>
                        <strong class="summary-value--primary">{{ formatCurrency(businessDeveloper.yearTotal) }}</strong>
                        <span>All selected business developer projects</span>
                    </div>
                    <div
                        class="business-developer-executive__metric business-developer-executive__metric--clickable"
                        role="button"
                        tabindex="0"
                        [title]="getSelectedBusinessDeveloperMarginTooltip()"
                        (click)="openSelectedBusinessDeveloperMarginOverview(businessDeveloperMarginOverviewModal)"
                        (keydown.enter)="openSelectedBusinessDeveloperMarginOverview(businessDeveloperMarginOverviewModal)">
                        <label>Portfolio margin</label>
                        <strong [ngClass]="{
                            'summary-value--success': getSelectedBusinessDeveloperRealizedMargin() >= 0,
                            'summary-value--danger': getSelectedBusinessDeveloperRealizedMargin() < 0
                        }">{{ formatCurrency(getSelectedBusinessDeveloperRealizedMargin()) }}</strong>
                        <span>All projects: revenue - delivery costs</span>
                    </div>
                    <div
                        class="business-developer-executive__metric business-developer-executive__metric--clickable"
                        role="button"
                        tabindex="0"
                        [title]="getSelectedBusinessDeveloperCostTooltip()"
                        (click)="openSelectedBusinessDeveloperCostOverview(businessDeveloperCostOverviewModal)"
                        (keydown.enter)="openSelectedBusinessDeveloperCostOverview(businessDeveloperCostOverviewModal)">
                        <label>Total cost {{ currentYear }}</label>
                        <strong>{{ formatCurrency(businessDeveloper.yearCostTotal) }}</strong>
                        <span>{{ getSelectedBusinessDeveloperCostSourceLabel() }} · click for breakdown</span>
                    </div>
                    <div class="business-developer-executive__metric">
                        <label>ROI result</label>
                        <strong [ngClass]="{
                            'summary-value--success': getSelectedBusinessDeveloperAchievement() >= 0,
                            'summary-value--danger': getSelectedBusinessDeveloperAchievement() < 0
                        }">{{ formatCurrency(getSelectedBusinessDeveloperAchievement()) }}</strong>
                        <span>Margin - person cost</span>
                    </div>
                    <div class="business-developer-executive__metric">
                        <label>ROI</label>
                        <strong [ngClass]="{
                            'summary-value--success': getSelectedBusinessDeveloperAchievement() >= 0,
                            'summary-value--danger': getSelectedBusinessDeveloperAchievement() < 0
                        }">{{ getSelectedBusinessDeveloperRoiLabel() }}</strong>
                        <span>ROI result / person cost</span>
                    </div>
                    <div class="business-developer-executive__metric">
                        <label>Break-even</label>
                        <strong>{{ getSelectedBusinessDeveloperBreakEvenLabel() }}</strong>
                        <span>At current phase pace</span>
                    </div>
                </div>
            </div>

            <div class="business-developer-meter__progress" *ngIf="businessDeveloper.yearTargetTotal > 0">
                <div
                    class="business-developer-meter__progress-bar"
                    [class.business-developer-meter__progress-bar--met]="businessDeveloper.yearTotal >= businessDeveloper.yearTargetTotal"
                    [style.width.%]="getTargetPercentage(businessDeveloper.yearTotal, businessDeveloper.yearTargetTotal)">
                </div>
            </div>
        </ng-container>

        <ng-template #businessDeveloperMarginOverviewModal let-modal>
            <div class="modal-wrapper margin-overview-modal">
                <div class="modal-header">
                    <h2 class="modal-title">
                        Portfolio margin — {{ selectedBusinessDeveloper?.businessDeveloperName }} {{ currentYear }}
                    </h2>
                    <button type="button" class="close" aria-label="Close" (click)="modal.dismiss()">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="margin-overview-modal__summary">
                        <div>
                            <label>Portfolio revenue</label>
                            <strong class="summary-value--primary">{{ formatCurrency(getSelectedBusinessDeveloperProjectRevenueTotal()) }}</strong>
                        </div>
                        <div>
                            <label>Portfolio delivery costs</label>
                            <strong>{{ formatCurrency(getSelectedBusinessDeveloperProjectCostTotal()) }}</strong>
                        </div>
                        <div>
                            <label>Portfolio margin</label>
                            <strong [ngClass]="{
                                'summary-value--success': getSelectedBusinessDeveloperRealizedMargin() >= 0,
                                'summary-value--danger': getSelectedBusinessDeveloperRealizedMargin() < 0
                            }">{{ formatCurrency(getSelectedBusinessDeveloperRealizedMargin()) }}</strong>
                        </div>
                    </div>

                    <div class="text--grey m-b-2">
                        This total is across all projects for this business developer. Delivery costs are based on project purchase invoices and payroll timesheets.
                    </div>

                    <div *ngIf="getSelectedBusinessDeveloperMarginDetails().length === 0" class="text--grey">
                        No project margin details available.
                    </div>

                    <div class="table__overflow" *ngIf="getSelectedBusinessDeveloperMarginDetails().length > 0">
                        <table class="table-view table-view--compact margin-overview-modal__table">
                            <thead>
                                <tr>
                                    <th>Project</th>
                                    <th class="text-right">Revenue</th>
                                    <th class="text-right">Delivery costs</th>
                                    <th class="text-right">Margin</th>
                                    <th>Cost source</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let detail of getSelectedBusinessDeveloperMarginDetails()">
                                    <td><strong>{{ detail.projectName || '-' }}</strong></td>
                                    <td class="text-right">{{ formatCurrency(detail.revenue) }}</td>
                                    <td class="text-right">{{ formatCurrency(detail.costs) }}</td>
                                    <td class="text-right">
                                        <strong [ngClass]="{
                                            'summary-value--success': detail.margin >= 0,
                                            'summary-value--danger': detail.margin < 0
                                        }">{{ formatCurrency(detail.margin) }}</strong>
                                    </td>
                                    <td>{{ detail.costSourceLabel || '-' }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><strong>Total</strong></td>
                                    <td class="text-right"><strong>{{ formatCurrency(getSelectedBusinessDeveloperProjectRevenueTotal()) }}</strong></td>
                                    <td class="text-right"><strong>{{ formatCurrency(getSelectedBusinessDeveloperProjectCostTotal()) }}</strong></td>
                                    <td class="text-right"><strong>{{ formatCurrency(getSelectedBusinessDeveloperRealizedMargin()) }}</strong></td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="button button--outline" (click)="modal.dismiss()">Close</button>
                </div>
            </div>
        </ng-template>

        <ng-template #businessDeveloperCostOverviewModal let-modal>
            <div class="modal-wrapper margin-overview-modal">
                <div class="modal-header">
                    <h2 class="modal-title">
                        Person cost — {{ selectedBusinessDeveloper?.businessDeveloperName }} {{ currentYear }}
                    </h2>
                    <button type="button" class="close" aria-label="Close" (click)="modal.dismiss()">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="margin-overview-modal__summary">
                        <div>
                            <label>Linked invoices</label>
                            <strong>{{ formatCurrency(getSelectedBusinessDeveloperLinkedInvoiceCostTotal()) }}</strong>
                        </div>
                        <div>
                            <label>Payroll budget</label>
                            <strong>{{ formatCurrency(getSelectedBusinessDeveloperPayrollCostTotal()) }}</strong>
                        </div>
                        <div>
                            <label>Total person cost</label>
                            <strong>{{ formatCurrency(selectedBusinessDeveloper?.yearCostTotal || 0) }}</strong>
                        </div>
                    </div>

                    <div class="text--grey m-b-2">
                        Freelance cost is based on received purchase invoices linked to the person. Employee cost is payroll budget plus linked purchase invoices.
                    </div>

                    <div *ngIf="getSelectedBusinessDeveloperCostDetails().length === 0" class="text--grey">
                        No person cost details available.
                    </div>

                    <div class="table__overflow" *ngIf="getSelectedBusinessDeveloperCostDetails().length > 0">
                        <table class="table-view table-view--compact margin-overview-modal__table">
                            <thead>
                                <tr>
                                    <th>Month</th>
                                    <th class="text-right">Linked invoices</th>
                                    <th class="text-right">Payroll budget</th>
                                    <th class="text-right">Total cost</th>
                                    <th>Cost source</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let detail of getSelectedBusinessDeveloperCostDetails()">
                                    <td><strong>{{ detail.monthLabel }}</strong></td>
                                    <td class="text-right">{{ formatCurrency(detail.linkedInvoiceCost) }}</td>
                                    <td class="text-right">{{ formatCurrency(detail.payrollCost) }}</td>
                                    <td class="text-right"><strong>{{ formatCurrency(detail.totalCost) }}</strong></td>
                                    <td>{{ detail.costSourceLabel || '-' }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><strong>Total</strong></td>
                                    <td class="text-right"><strong>{{ formatCurrency(getSelectedBusinessDeveloperLinkedInvoiceCostTotal()) }}</strong></td>
                                    <td class="text-right"><strong>{{ formatCurrency(getSelectedBusinessDeveloperPayrollCostTotal()) }}</strong></td>
                                    <td class="text-right"><strong>{{ formatCurrency(selectedBusinessDeveloper?.yearCostTotal || 0) }}</strong></td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="button button--outline" (click)="modal.dismiss()">Close</button>
                </div>
            </div>
        </ng-template>
    </section>

    <!-- Summary -->
    <div class="d-flex m-b-4" style="gap: 2rem; flex-wrap: wrap;" *ngIf="isAllBusinessDevelopersSelected()">
        <div class="box">
            <label class="m-b-1">Total Revenue {{ currentYear }}</label>
            <div class="summary-value summary-value--primary">{{ formatCurrency(getGrandTotal()) }}</div>
        </div>
        <div class="box" *ngIf="getGrandTargetTotal() > 0">
            <label class="m-b-1">Total Target {{ currentYear }}</label>
            <div class="summary-value">{{ formatCurrency(getGrandTargetTotal()) }}</div>
        </div>
        <div class="box" *ngIf="getGrandTargetTotal() > 0">
            <label class="m-b-1">Achievement</label>
            <div class="summary-value" [ngClass]="{
                'summary-value--success': getGrandTotal() >= getGrandTargetTotal(),
                'summary-value--danger': getGrandTotal() < getGrandTargetTotal()
            }">
                {{ getTargetPercentage(getGrandTotal(), getGrandTargetTotal()) }}%
            </div>
        </div>
    </div>

    <!-- Legend -->
    <div class="legend m-b-3">
        <span class="legend-item">
            <span class="legend-dot legend-dot--met"></span> Target met
        </span>
        <span class="legend-item">
            <span class="legend-dot legend-dot--missed"></span> Target missed (past)
        </span>
        <span class="legend-item">
            <span class="legend-dot legend-dot--future"></span> Target pending (current/future)
        </span>
        <span class="legend-item">
            <span class="legend-dot legend-dot--none"></span> No target set
        </span>
    </div>

    <!-- Matrix Table -->
    <div class="table__scroll">
        <table class="table-view table-view--compact turnover-matrix">
            <thead>
                <tr>
                    <th class="division-header">{{ isAllBusinessDevelopersSelected() ? 'Division' : 'Business developer' }}</th>
                    <th *ngFor="let month of monthLabels; let i = index"
                        [class.current-month]="i === currentMonth">
                        {{ month }}
                    </th>
                    <th class="year-total-header">Year Total</th>
                </tr>
            </thead>
            <tbody>
                <!-- Division Rows -->
                <ng-container *ngIf="isAllBusinessDevelopersSelected()">
                <ng-container *ngFor="let div of overviewData; trackBy: trackByDivision">
                    <tr>
                        <td class="division-name">
                            <strong>{{ div.divisionLabel }}</strong>
                        </td>
                        <td *ngFor="let month of monthLabels; let i = index"
                            class="turnover-cell"
                            [ngClass]="getCellClass(div.division, i)"
                            [class.current-month]="i === currentMonth"
                            (click)="openDetailModal(div.division, i)">
                            <!-- Yuki per-cell verification badge -->
                            <span class="cell-yuki-badge cell-yuki-badge--ok"
                                  *ngIf="getMonthYukiStatus(div.division, i) === 'ok'"
                                  [title]="getMonthYukiTooltip(div.division, i)">
                                <i class="las la-check-circle"></i>
                            </span>
                            <span class="cell-yuki-badge cell-yuki-badge--fail"
                                  *ngIf="getMonthYukiStatus(div.division, i) === 'mismatch'"
                                  [title]="getMonthYukiTooltip(div.division, i)">
                                <i class="las la-times-circle"></i>
                            </span>
                            <div class="cell-content">
                                <span class="cell-amount">{{ formatCurrency(getTurnover(div.division, i)) }}</span>
                                <span class="cell-target" *ngIf="getTarget(div.division, i) > 0">
                                    / {{ formatCurrency(getTarget(div.division, i)) }}
                                </span>
                            </div>
                            <!-- Mini progress bar -->
                            <div class="cell-progress" *ngIf="getTarget(div.division, i) > 0">
                                <div class="cell-progress-bar"
                                    [style.width.%]="getTargetPercentage(getTurnover(div.division, i), getTarget(div.division, i))"
                                    [ngClass]="{
                                        'progress-met': getTurnover(div.division, i) >= getTarget(div.division, i),
                                        'progress-missed': getCellClass(div.division, i) === 'target-missed',
                                        'progress-future': getCellClass(div.division, i) === 'target-future'
                                    }">
                                </div>
                            </div>
                        </td>
                        <td class="year-total-cell clickable" [ngClass]="getYearTotalClass(div.division)"
                            (click)="openYearDetailModal(div.division)">
                            <strong>{{ formatCurrency(getDivisionYearTotal(div.division)) }}</strong>
                            <span class="cell-target" *ngIf="getDivisionYearTarget(div.division) > 0">
                                / {{ formatCurrency(getDivisionYearTarget(div.division)) }}
                            </span>
                        </td>
                    </tr>
                </ng-container>
                </ng-container>
                <tr *ngIf="!isAllBusinessDevelopersSelected() && selectedBusinessDeveloper as businessDeveloper">
                    <td class="division-name">
                        <strong>{{ businessDeveloper.businessDeveloperName }}</strong>
                    </td>
                    <td *ngFor="let month of monthLabels; let i = index"
                        class="turnover-cell"
                        [ngClass]="getBusinessDeveloperMonthClass(i)"
                        [class.current-month]="i === currentMonth"
                        (click)="openBusinessDeveloperMonthDetailModal(i)">
                        <div class="cell-content">
                            <span class="cell-amount">{{ formatCurrency(getSelectedBusinessDeveloperMonthTurnover(i)) }}</span>
                            <span class="cell-target" *ngIf="getSelectedBusinessDeveloperMonthTarget(i) > 0">
                                / {{ formatCurrency(getSelectedBusinessDeveloperMonthTarget(i)) }}
                            </span>
                            <span class="cell-margin" *ngIf="shouldShowMonthMargin(i)" [ngClass]="{
                                'cell-margin--success': getSelectedBusinessDeveloperMonthRealizedMargin(i) >= 0,
                                'cell-margin--danger': getSelectedBusinessDeveloperMonthRealizedMargin(i) < 0
                            }">
                                Margin {{ formatCurrency(getSelectedBusinessDeveloperMonthRealizedMargin(i)) }}
                            </span>
                        </div>
                        <div class="cell-progress" *ngIf="getSelectedBusinessDeveloperMonthTarget(i) > 0">
                            <div class="cell-progress-bar"
                                [style.width.%]="getTargetPercentage(getSelectedBusinessDeveloperMonthTurnover(i), getSelectedBusinessDeveloperMonthTarget(i))"
                                [ngClass]="{
                                    'progress-met': getSelectedBusinessDeveloperMonthTurnover(i) >= getSelectedBusinessDeveloperMonthTarget(i),
                                    'progress-missed': getBusinessDeveloperMonthClass(i) === 'target-missed',
                                    'progress-future': getBusinessDeveloperMonthClass(i) === 'target-future'
                                }">
                            </div>
                        </div>
                    </td>
                    <td class="year-total-cell clickable" [ngClass]="getSelectedBusinessDeveloperYearClass()"
                        (click)="openBusinessDeveloperYearDetailModal()">
                        <strong>{{ formatCurrency(businessDeveloper.yearTotal) }}</strong>
                        <span class="cell-target" *ngIf="businessDeveloper.yearTargetTotal > 0">
                            / {{ formatCurrency(businessDeveloper.yearTargetTotal) }}
                        </span>
                        <span class="cell-margin" [ngClass]="{
                            'cell-margin--success': getSelectedBusinessDeveloperRealizedMargin() >= 0,
                            'cell-margin--danger': getSelectedBusinessDeveloperRealizedMargin() < 0
                        }">
                            Margin {{ formatCurrency(getSelectedBusinessDeveloperRealizedMargin()) }}
                        </span>
                    </td>
                </tr>
            </tbody>
            <tfoot *ngIf="isAllBusinessDevelopersSelected()">
                <!-- Totals Row -->
                <tr class="totals-row">
                    <td class="division-name"><strong>Total</strong></td>
                    <td *ngFor="let month of monthLabels; let i = index"
                        class="turnover-cell totals-cell clickable"
                        [ngClass]="getMonthTotalClass(i)"
                        [class.current-month]="i === currentMonth"
                        (click)="openTotalMonthDetailModal(i)">
                        <span class="cell-yuki-badge cell-yuki-badge--ok"
                              *ngIf="getTotalMonthYukiStatus(i) === 'ok'"
                              [title]="getTotalMonthYukiTooltip(i)">
                            <i class="las la-check-circle"></i>
                        </span>
                        <span class="cell-yuki-badge cell-yuki-badge--fail"
                              *ngIf="getTotalMonthYukiStatus(i) === 'mismatch'"
                              [title]="getTotalMonthYukiTooltip(i)">
                            <i class="las la-times-circle"></i>
                        </span>
                        <div class="cell-content">
                            <strong class="cell-amount">{{ formatCurrency(getMonthTotal(i)) }}</strong>
                            <span class="cell-target" *ngIf="getMonthTargetTotal(i) > 0">
                                / {{ formatCurrency(getMonthTargetTotal(i)) }}
                            </span>
                            <span class="cell-margin" *ngIf="hasMonthMarginTotal(i)" [ngClass]="{
                                'cell-margin--success': getMonthMarginTotal(i) >= 0,
                                'cell-margin--danger': getMonthMarginTotal(i) < 0
                            }">
                                Profit/Loss {{ formatCurrency(getMonthMarginTotal(i)) }}
                            </span>
                        </div>
                    </td>
                    <td class="year-total-cell totals-cell clickable"
                        (click)="openTotalYearDetailModal()">
                        <span class="cell-yuki-badge cell-yuki-badge--ok"
                              *ngIf="getTotalYearYukiStatus() === 'ok'"
                              [title]="getTotalYearYukiTooltip()">
                            <i class="las la-check-circle"></i>
                        </span>
                        <span class="cell-yuki-badge cell-yuki-badge--fail"
                              *ngIf="getTotalYearYukiStatus() === 'mismatch'"
                              [title]="getTotalYearYukiTooltip()">
                            <i class="las la-times-circle"></i>
                        </span>
                        <strong>{{ formatCurrency(getGrandTotal()) }}</strong>
                        <span class="cell-target" *ngIf="getGrandTargetTotal() > 0">
                            / {{ formatCurrency(getGrandTargetTotal()) }}
                        </span>
                    </td>
                </tr>
            </tfoot>
        </table>
    </div>

    <div class="business-developer-period-note m-t-2" *ngIf="!isAllBusinessDevelopersSelected() && selectedBusinessDeveloper">
        <strong>Portfolio period:</strong> {{ getSelectedBusinessDeveloperPortfolioPeriodLabel() }}
        <span class="business-developer-period-note__separator">·</span>
        <strong>Person cost period:</strong> {{ getSelectedBusinessDeveloperPersonCostPeriodLabel() }}
    </div>

    <!-- Yuki Check indicator -->
    <div class="m-t-3 d-flex align-items-center" style="gap: 1rem;">
        <!-- Yuki check: ✓ / ✗ indicator -->
        <span class="yuki-check" *ngIf="!loadingReconciliation && yukiCheckDone">
            <span *ngIf="yukiCheckOk" class="yuki-check--ok" title="All invoices linked to projects">
                <i class="las la-check-circle"></i> Yuki OK
            </span>
            <span *ngIf="!yukiCheckOk" class="yuki-check--fail cursor-pointer"
                  (click)="toggleDiscrepancies()"
                  title="Click to see unlinked invoices">
                <i class="las la-times-circle"></i> {{ yukiDiscrepancyCount }} unlinked
            </span>
        </span>
        <span *ngIf="loadingReconciliation" class="yuki-check yuki-check--loading">
            <i class="las la-spinner la-spin"></i> Checking…
        </span>
    </div>

    <!-- Discrepancies (unlinked invoices) -->
    <div *ngIf="showDiscrepancies && reconciliationResult && reconciliationResult.missingInResumo.length > 0" class="discrepancy-panel m-t-2">
        <div class="d-flex align-items-center m-b-2">
            <h6 class="m-b-0 text-warning">
                <i class="las la-exclamation-triangle m-r-1"></i>
                Unlinked Sales Invoices
            </h6>
            <span class="text-muted small m-l-2">These invoices are not linked to any project</span>
        </div>
        <div class="table__scroll">
            <table class="table-view table-view--compact reconciliation-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Invoice #</th>
                        <th>Subject</th>
                        <th>Contact</th>
                        <th class="text-right">Amount</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let item of reconciliationResult.missingInResumo"
                        [class.credit-note-row]="item.amount < 0"
                        class="clickable-row"
                        (click)="goToInvoice(item.yukiId)">
                        <td>{{ item.documentDate || '-' }}</td>
                        <td>{{ item.invoiceNumber || '-' }}</td>
                        <td>
                            {{ item.subject || '-' }}
                            <span class="badge badge--info m-l-1" *ngIf="item.amount < 0">Creditnota</span>
                        </td>
                        <td>{{ item.contactName || '-' }}</td>
                        <td class="text-right">
                            <strong [class.text-danger]="item.amount < 0">{{ formatCurrency(item.amount) }}</strong>
                        </td>
                        <td class="text-right">
                            <i class="las la-external-link-alt" title="Go to invoice"></i>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</div>
